import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import { fetchSupplierDetail, getInventoryOwnerContext } from './inventoryApi';
import { extractRestaurantName, fetchRestaurantById } from './restaurantApi';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Inventory.css';

const PURCHASE_PATH = '/home?tab=purchase';

const displayValue = (value) => {
  const text = value != null ? String(value).trim() : '';
  return text || '—';
};

function formatActiveLabel(v) {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return null;
  if (typeof v === 'boolean') return v ? 'Active' : 'Inactive';
  const s = String(v).trim().toLowerCase();
  if (v === 1 || v === '1' || s === 'yes' || s === 'y' || s === 'active' || s === 'true') {
    return 'Active';
  }
  if (v === 0 || v === '0' || s === 'no' || s === 'n' || s === 'inactive' || s === 'false') {
    return 'Inactive';
  }
  return String(v);
}

function activeBadgeClass(v) {
  const label = formatActiveLabel(v);
  if (label === 'Active') return 'active';
  if (label === 'Inactive') return 'inactive';
  return 'unknown';
}

const DetailField = ({ label, value, wide = false, badge = false, children }) => (
  <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
    <span className="restaurant-detail-label">{label}</span>
    {children ??
      (badge ? (
        <span
          className={`restaurant-detail-badge restaurant-detail-badge-${activeBadgeClass(value)}`}
        >
          {displayValue(formatActiveLabel(value))}
        </span>
      ) : (
        <span className="restaurant-detail-value">{displayValue(value)}</span>
      ))}
  </div>
);

const SupplierDetail = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { enabledTypes: enabledServiceTypes } = useRestaurantEnabledServices();
  const [record, setRecord] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [ownerLabel, setOwnerLabel] = useState('Restaurant');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const rawId = supplierId != null ? String(supplierId).trim() : '';
    const idNum = Number(rawId);

    if (!rawId || Number.isNaN(idNum)) {
      setLoading(false);
      setError('Invalid supplier id.');
      setRecord(null);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError('');
      setRecord(null);
      setOwnerName('');
      setOwnerLabel('Restaurant');
      try {
        const owner = getInventoryOwnerContext();
        const data = await fetchSupplierDetail(idNum, owner);
        if (cancelled) return;
        if (!data || typeof data !== 'object') {
          setError('No data returned for this supplier.');
          return;
        }
        setRecord(data);

        const sid = data.storeId ?? data.Store_Id;
        const rid = data.restaurantId ?? data.Restaurant_Id;
        if (sid != null) {
          setOwnerLabel('Store');
          let resolvedStoreName = data.storeName ?? data.Store_Name ?? '';
          const storedSid = localStorage.getItem('storeId');
          const storedStoreName = localStorage.getItem('storeName') || '';
          if (
            !resolvedStoreName &&
            storedSid &&
            String(sid) === String(storedSid) &&
            storedStoreName.trim()
          ) {
            resolvedStoreName = storedStoreName.trim();
          }
          if (!cancelled) {
            setOwnerName(resolvedStoreName || `Store ${sid}`);
          }
          return;
        }

        let resolvedRestaurantName = data.restaurantName ?? data.Restaurant_Name ?? '';
        const storedRid = localStorage.getItem('restaurantId');
        const storedName = localStorage.getItem('restaurantName') || '';
        if (!resolvedRestaurantName && rid != null && storedRid && String(rid) === String(storedRid) && storedName.trim()) {
          resolvedRestaurantName = storedName.trim();
        } else if (!resolvedRestaurantName && rid) {
          try {
            const restaurant = await fetchRestaurantById(rid);
            resolvedRestaurantName = extractRestaurantName(restaurant);
          } catch {
            // fallback below
          }
        }

        if (!cancelled) {
          setOwnerName(resolvedRestaurantName || (rid != null ? `Restaurant ${rid}` : ''));
        }
      } catch (e) {
        if (cancelled) return;
        console.error(
          'Supplier detail fetch failed:',
          e?.response?.status,
          e?.response?.data ?? e?.message
        );
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            'Failed to load supplier details.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supplierId]);

  const supplierName = record?.supplierName ?? record?.Supplier_Name;

  const email = record?.email ?? record?.Email;

  return (
    <RestaurantDashboardLayout activeTab="purchase" enabledServiceTypes={enabledServiceTypes}>
      <div className="supplier-detail-page">
        <div className="restaurant-detail-panel">
          <div className="restaurant-detail-card">
            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
              <div className="inventory-item-detail-header-row">
                <button
                  type="button"
                  className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                  onClick={() => navigate(PURCHASE_PATH)}
                  aria-label="Back to purchase"
                >
                  <FaArrowLeft aria-hidden />
                </button>
                <h3 className="restaurant-detail-section-title">Supplier profile</h3>
              </div>
            </div>

            {loading ? (
              <p className="order-details-loading">Loading supplier details…</p>
            ) : null}

            {!loading && error ? (
              <p className="restaurant-detail-message error">{error}</p>
            ) : null}

            {!loading && !error && record ? (
              <div className="restaurant-detail-grid">
                <DetailField
                  label="Supplier ID"
                  value={record.supplierId ?? record.Supplier_Id}
                />
                <DetailField label="Supplier name" value={supplierName} />
                <DetailField
                  label="Contact person"
                  value={record.contactPerson ?? record.Contact_Person}
                />
                <DetailField label="Status" value={record.isActive ?? record.Is_Active} badge />
                <DetailField label={ownerLabel} value={ownerName} />
                <DetailField
                  label="GST number"
                  value={
                    record.gstNumber ?? record.Gst_Number ?? record.GST_Number
                  }
                />
              </div>
            ) : null}

            {!loading && !record && !error ? (
              <p className="order-details-empty">No detail data for this supplier.</p>
            ) : null}
          </div>

          {!loading && !error && record ? (
            <div className="restaurant-detail-card">
              <div className="restaurant-detail-card-header">
                <h3 className="restaurant-detail-section-title">Contact</h3>
              </div>
              <div className="restaurant-detail-grid">
                <DetailField label="Email">
                  {email && String(email).trim() ? (
                    <a
                      className="restaurant-detail-value"
                      href={`mailto:${String(email).trim()}`}
                      style={{ color: '#003771', textDecoration: 'none' }}
                    >
                      {String(email).trim()}
                    </a>
                  ) : (
                    <span className="restaurant-detail-value">—</span>
                  )}
                </DetailField>
                <DetailField label="Mobile" value={formatMobileDisplayOrFallback(record.mobile ?? record.Mobile, '—')} />
                <DetailField label="Alternate number#" value={formatMobileDisplayOrFallback(record.altNo ?? record.Alt_No, '—')} />
                <DetailField
                  label="Address"
                  value={record.address ?? record.Address}
                  wide
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </RestaurantDashboardLayout>
  );
};

export default SupplierDetail;
